import { injectable } from "inversify";
import IValidationRuleService from "./IValidationRuleService";
import ValidationRule from "./rules/ValidationRule";
import { ValidationRuleType } from "./rules/validationRuleType";
import ValidationService from "./ValidationService";
import PatternValidatonRule from './rules/PatternValidatonRule';
import FunctionValidationRule from './rules/FunctionValidationRule';
import FilesValidationRule from './rules/FilesValidationRule';
import PhoneNumberValidatonRule from './rules/PhoneNumberValidatonRule';
import { EmptyErrorValidationResult, SuccessValidationResult, ValidationResult } from './models/ValidationResult';
import PhoneNumberValidator from './validators/PhoneNumberValidator';
import EmailValidationRule from "./rules/EmailValidationRule";
import NumericValidationRule from "./rules/NumericValidationRule";
import TextValidationRule from "./rules/TextValidationRule";
import IbanValidationRule from "./rules/IbanValidationRule";
import DateNotInPastValidationRule from "./rules/DateNotInPastValidationRule";
import { isValidIBAN } from "ibantools";
import { TFunction } from "i18next";

@injectable()
export default class ValidationRuleService implements IValidationRuleService {
  private readonly _validationService: ValidationService;
  private readonly _phoneValidator: PhoneNumberValidator;

  constructor() {
    this._validationService = new ValidationService();
    this._phoneValidator = new PhoneNumberValidator();
  }

  validate<T>(rule: ValidationRule, value: T, translate: TFunction): ValidationResult {
    switch (rule.type) {
      case ValidationRuleType.OnlyRequired:
        return this.validateRequiredRule(rule, value, translate);
      case ValidationRuleType.Pattern:
        const validatePattern = (patternRule: PatternValidatonRule, val: string) => this._validationService.validateRegex(val, patternRule.regex, patternRule.enableEmptyValue);
        return this.validateRequiredAndRule<any, PatternValidatonRule>(rule as PatternValidatonRule, value, validatePattern, translate);
      case ValidationRuleType.Function:
        const validateFunction = (functionRule: FunctionValidationRule, val: T) => functionRule.validate(val);
        return this.validateRequiredAndRule<any, FunctionValidationRule>(rule as FunctionValidationRule, value, validateFunction, translate);
      case ValidationRuleType.Files:
        const validateFiles = (filesRule: FilesValidationRule, val: FileList) => this._validationService.validateFiles(val, filesRule.filesCount);
        return this.validateRequiredAndRule<any, FilesValidationRule>(rule as FilesValidationRule, value, validateFiles, translate);
      case ValidationRuleType.PhoneNumber:
        const validatePhone = (phoneRule: PhoneNumberValidatonRule, val: string) => this._phoneValidator.validate(val, phoneRule.prefix);
        return this.validateRequiredAndRule<any, PhoneNumberValidatonRule>(rule as PhoneNumberValidatonRule, value, validatePhone, translate);
      case ValidationRuleType.Email:
        const validateEmail = (emailRule: EmailValidationRule, val: string) => this._validationService.ValidateEmail(val);
        return this.validateRequiredAndRule<any, EmailValidationRule>(rule as EmailValidationRule, value, validateEmail, translate);
      case ValidationRuleType.Numeric:
        const validateNumeric = (numericRule: NumericValidationRule, val: string) => this._validationService.validateNumber(Number(String(val).replace(',', '.')));
        return this.validateRequiredAndRule<any, NumericValidationRule>(rule as NumericValidationRule, value, validateNumeric, translate);
      case ValidationRuleType.Text:
        const validateText = (textRule: TextValidationRule, val: string) => this._validationService.validateString(val);
        return this.validateRequiredAndRule<any, TextValidationRule>(rule as TextValidationRule, value, validateText, translate);
      case ValidationRuleType.IBAN:
        const validateIban = (ibanRule: IbanValidationRule, val: string) => isValidIBAN(val);
        return this.validateRequiredAndRule<any, IbanValidationRule>(rule as IbanValidationRule, value, validateIban, translate);
      case ValidationRuleType.DateNotInPast:
        const validateDateNotInPast = (dateRule: DateNotInPastValidationRule, val: any) => this._validationService.validateDateNotInPast(val);
        return this.validateRequiredAndRule<any, DateNotInPastValidationRule>(rule as DateNotInPastValidationRule, value, validateDateNotInPast, translate);
      default:
        return {...EmptyErrorValidationResult};
    }
  }

  // PRIVATE
  private validateRequiredAndRule<TValue, TValidator extends ValidationRule>(
    rule: TValidator,
    value: TValue,
    validationFce: (rule: TValidator, value: TValue) => boolean,
    translate: TFunction
  ): ValidationResult {
    let result = this.validateRequiredRule(rule, value, translate);
    if (result.isValid) {
      const isValid = validationFce(rule, value);

      if (!isValid) {
        let validationResult: ValidationResult = {...EmptyErrorValidationResult};
        validationResult.errorMessage = rule.message ? translate(rule.message) : translate("invalid");

        return validationResult;
      }
    }

    return result;
  }

  private validateRequiredRule<TValue>(rule: ValidationRule, value: TValue, translate: TFunction): ValidationResult {
    if (rule.required) {
      const isValid = this._validationService.validateReuired(value);

      if (!isValid) {
        let validationResult:ValidationResult = {...EmptyErrorValidationResult};
        validationResult.errorMessage = rule.requiredMessage ? translate(rule.requiredMessage) : translate("required");

        return validationResult;
      }
    }

    return SuccessValidationResult;   
  }
}
